import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';

/* Scientific Notation Calculator — 3 sub-tools matching Calculator.net */
export default function ScientificNotationCalc() {
  const [tab, setTab] = useState('arith');
  return (
    <CalcShell look="cnet" showActions={false} title="Scientific Notation Calculator" subtitle="Arithmetic on scientific-notation numbers plus decimal ↔ scientific conversion in both directions.">
      <div className="flex gap-2 mb-0 flex-wrap">
        {[['arith', 'Scientific Notation Calculator'], ['dec2sci', 'Decimal → Scientific'], ['sci2dec', 'Scientific → Decimal']].map(([v, l]) => (
          <button key={v} data-testid={`sn-tab-${v}`}
            className={`cnet-tab ${tab === v ? 'active' : ''}`}
            onClick={() => setTab(v)}>{l}</button>
        ))}
      </div>
      <div className="cnet-tab-body">
        {tab === 'arith' && <ArithSection />}
        {tab === 'dec2sci' && <DecToSciSection />}
        {tab === 'sci2dec' && <SciToDecSection />}
      </div>
    </CalcShell>
  );
}

function fmtSci(x, digits = 6) {
  if (!isFinite(x)) return String(x);
  if (x === 0) return '0';
  const e = Math.floor(Math.log10(Math.abs(x)));
  const coef = x / Math.pow(10, e);
  return `${coef.toFixed(digits)} × 10^${e}`;
}

/* Sub 1: arithmetic on two scientific-notation numbers */
function ArithSection() {
  const [a, setA] = useState('1.23');
  const [ea, setEa] = useState(4);
  const [op, setOp] = useState('+');
  const [b, setB] = useState('4.56');
  const [eb, setEb] = useState(2);

  const r = useMemo(() => {
    const A = (parseFloat(a) || 0) * Math.pow(10, parseInt(ea) || 0);
    const B = (parseFloat(b) || 0) * Math.pow(10, parseInt(eb) || 0);
    let val;
    if (op === '+') val = A + B;
    else if (op === '-') val = A - B;
    else if (op === '*') val = A * B;
    else if (op === '/') val = B === 0 ? Infinity : A / B;
    else val = 0;
    const eng = (() => {
      if (!isFinite(val) || val === 0) return String(val);
      const e = Math.floor(Math.log10(Math.abs(val)));
      const engExp = Math.floor(e / 3) * 3;
      const engCoef = val / Math.pow(10, engExp);
      return `${engCoef.toFixed(6)} × 10^${engExp}`;
    })();
    return { A, B, val, sci: fmtSci(val), eng, eNot: isFinite(val) ? val.toExponential(6) : String(val) };
  }, [a, ea, op, b, eb]);

  return (
    <>
      <p className="text-sm text-slate-700 mb-3">Please enter two numbers in scientific notation and choose an operator to compute the result in scientific, engineering and standard decimal form.</p>
      <div className="cnet-panel">
        <div className="flex flex-wrap items-center gap-2">
          <input data-testid="sn-arith-a" className="cnet-input w-28" type="number" step="any" value={a} onChange={e => setA(e.target.value)} />
          <span className="text-slate-600">×&nbsp;10^</span>
          <input data-testid="sn-arith-ea" className="cnet-input w-20" type="number" value={ea} onChange={e => setEa(e.target.value)} />
          <select data-testid="sn-arith-op" className="cnet-select w-16" value={op} onChange={e => setOp(e.target.value)}>
            <option value="+">+</option><option value="-">−</option>
            <option value="*">×</option><option value="/">÷</option>
          </select>
          <input data-testid="sn-arith-b" className="cnet-input w-28" type="number" step="any" value={b} onChange={e => setB(e.target.value)} />
          <span className="text-slate-600">×&nbsp;10^</span>
          <input data-testid="sn-arith-eb" className="cnet-input w-20" type="number" value={eb} onChange={e => setEb(e.target.value)} />
        </div>
      </div>
      <table className="cnet-info-table mt-3" data-testid="sn-arith-result">
        <tbody>
          <tr><td>Result (decimal)</td><td className="text-right font-bold text-[#4c7b25]">{r.val}</td></tr>
          <tr><td>Scientific notation</td><td className="text-right font-mono">{r.sci}</td></tr>
          <tr><td>Engineering notation</td><td className="text-right font-mono">{r.eng}</td></tr>
          <tr><td>E notation</td><td className="text-right font-mono">{r.eNot}</td></tr>
        </tbody>
      </table>
      <StepsPanel steps={[
        `A = ${a} × 10^${ea} = ${r.A}`,
        `B = ${b} × 10^${eb} = ${r.B}`,
        `A ${op === '*' ? '×' : op === '/' ? '÷' : op} B = ${r.val}`,
        `Scientific form: ${r.sci}`,
      ]} />
    </>
  );
}

function DecToSciSection() {
  const [dec, setDec] = useState('12345.678');
  const c = useMemo(() => {
    const n = parseFloat(dec) || 0;
    if (n === 0) return { coef: 0, exp: 0, ePlus: '0', engineering: '0', sci: '0' };
    const e = Math.floor(Math.log10(Math.abs(n)));
    const coef = n / Math.pow(10, e);
    const eng = Math.floor(e / 3) * 3;
    const engCoef = n / Math.pow(10, eng);
    return { coef, exp: e, ePlus: n.toExponential(), engineering: `${engCoef.toFixed(6)} × 10^${eng}`, sci: `${coef.toFixed(6)} × 10^${e}` };
  }, [dec]);
  return (
    <>
      <p className="text-sm text-slate-700 mb-3">Enter a decimal number to convert it to scientific, engineering and E-notation form.</p>
      <div className="cnet-panel">
        <div className="cnet-row-2col"><div className="cnet-label">Decimal number</div>
          <input data-testid="sn-dec" className="cnet-input" value={dec} onChange={e => setDec(e.target.value)} />
        </div>
      </div>
      <table className="cnet-info-table mt-3">
        <tbody>
          <tr><td>Scientific Notation</td><td className="text-right font-bold text-[#4c7b25]" data-testid="sn-sci">{c.sci}</td></tr>
          <tr><td>E Notation</td><td className="text-right font-mono" data-testid="sn-e">{c.ePlus}</td></tr>
          <tr><td>Engineering Notation</td><td className="text-right" data-testid="sn-eng">{c.engineering}</td></tr>
          <tr><td>Coefficient</td><td className="text-right">{typeof c.coef === 'number' ? c.coef.toFixed(6) : c.coef}</td></tr>
          <tr><td>Exponent</td><td className="text-right">{c.exp}</td></tr>
        </tbody>
      </table>
    </>
  );
}

function SciToDecSection() {
  const [coef, setCoef] = useState('1.2345678');
  const [exp, setExp] = useState('4');
  const c = useMemo(() => {
    const co = parseFloat(coef) || 0;
    const e = parseInt(exp) || 0;
    const dec = co * Math.pow(10, e);
    return { decimal: dec.toString(), engineering: isFinite(dec) ? dec.toExponential(6) : String(dec) };
  }, [coef, exp]);
  return (
    <>
      <p className="text-sm text-slate-700 mb-3">Enter a coefficient <b>a</b> and exponent <b>b</b> to convert a × 10<sup>b</sup> to standard decimal.</p>
      <div className="cnet-panel">
        <div className="cnet-row-2col"><div className="cnet-label">Coefficient (a)</div>
          <input data-testid="sn-coef" className="cnet-input" value={coef} onChange={e => setCoef(e.target.value)} />
        </div>
        <div className="cnet-row-2col"><div className="cnet-label">Exponent (b)</div>
          <input data-testid="sn-exp" className="cnet-input" type="number" value={exp} onChange={e => setExp(e.target.value)} />
        </div>
      </div>
      <table className="cnet-info-table mt-3">
        <tbody>
          <tr><td>Decimal</td><td className="text-right font-bold text-[#4c7b25]" data-testid="sn-dec-result">{c.decimal}</td></tr>
          <tr><td>E notation</td><td className="text-right font-mono">{c.engineering}</td></tr>
        </tbody>
      </table>
    </>
  );
}
